/* eslint-disable react/no-unescaped-entities */
import { Building2, Trophy, BookOpen, MapPin, CheckCircle2, ArrowRight, GraduationCap } from 'lucide-react';
import { Link } from 'react-router-dom';
import GermanyResourceShell from './_components/GermanyResourceShell';

const DESC = 'Germany public universities 2026 for Indian students: tuition-free TU9 list, admission requirements, rankings, English-taught Masters, and how to apply.';

const TU9 = [
  { name: 'TU Munich (TUM)',                city: 'Munich',    state: 'Bavaria',         strength: 'Engineering, CS, Aerospace, Medicine', rank: '#37 (QS 2026)' },
  { name: 'RWTH Aachen University',         city: 'Aachen',    state: 'NRW',             strength: 'Mechanical, Electrical, CS, Materials', rank: '#106 (QS 2026)' },
  { name: 'KIT (Karlsruhe Institute of Tech)', city: 'Karlsruhe', state: 'Baden-Württem.', strength: 'Physics, CS, Mechanical Engg', rank: '#119 (QS 2026)' },
  { name: 'TU Berlin',                      city: 'Berlin',    state: 'Berlin',          strength: 'EE, Civil, Urban Planning, Energy', rank: '#154 (QS 2026)' },
  { name: 'TU Darmstadt',                   city: 'Darmstadt', state: 'Hesse',           strength: 'CS, Mechatronics, Industrial Engg', rank: '#247 (QS 2026)' },
  { name: 'TU Dresden',                     city: 'Dresden',   state: 'Saxony',          strength: 'CS, Electrical Engg, Microelectronics', rank: '#250 (QS 2026)' },
  { name: 'Leibniz Universität Hannover',   city: 'Hannover',  state: 'Lower Saxony',    strength: 'Mechanical, Optics, Power Engg', rank: '#346 (QS 2026)' },
  { name: 'TU Braunschweig',                city: 'Braunschweig', state: 'Lower Saxony', strength: 'Aerospace, Automotive Engg', rank: '#~400 (QS 2026)' },
  { name: 'University of Stuttgart',        city: 'Stuttgart', state: 'Baden-Württem.',  strength: 'Mechanical, Automotive, Aerospace', rank: '#312 (QS 2026)' },
];

const TOP_NON_TU9 = [
  { name: 'LMU Munich',                  type: 'Research Uni',  strength: 'Medicine, Physics, Economics, Humanities' },
  { name: 'University of Heidelberg',    type: 'Research Uni',  strength: 'Medicine, Biology, Law, Philosophy' },
  { name: 'Humboldt University Berlin',  type: 'Research Uni',  strength: 'Humanities, Social Sciences, Physics' },
  { name: 'Free University Berlin',      type: 'Research Uni',  strength: 'Political Science, Law, Biology, Math' },
  { name: 'University of Tübingen',      type: 'Research Uni',  strength: 'Theology, Medicine, AI, Linguistics' },
  { name: 'Goethe University Frankfurt', type: 'Research Uni',  strength: 'Economics, Finance, Law, Psychology' },
];

const ADMISSION_STEPS = [
  { step: 1, title: 'Choose your programme',                  body: 'Use the DAAD International Programmes Database (daad.de/iam/de) — filter by "English-taught Masters" + your subject area.' },
  { step: 2, title: 'Get APS Certificate',                    body: 'Mandatory for ALL Indian applicants. Process takes 4–8 weeks. ' },
  { step: 3, title: 'Language proof (English OR German)',     body: 'IELTS 6.5 OR TOEFL 90+ for English programmes; TestDaF / DSH / Goethe B2 for German programmes.' },
  { step: 4, title: 'Submit via Uni-Assist OR direct',        body: '70% of public universities use Uni-Assist as the central application portal. Some apply directly (TU Munich, RWTH Aachen). Application fee €75–€150 per uni.' },
  { step: 5, title: 'Receive admission letter (Zulassung)',    body: 'Decision in 4–8 weeks. Conditional offers may require provisional GPA upgrades or final IELTS scores.' },
  { step: 6, title: 'Open blocked account + apply for visa',   body: 'After admission, open Sperrkonto and file visa. See related guides on Blocked Account and Student Visa.' },
];

const FAQS = [
  { q: 'Which is the best public university in Germany for Indian students?',
    a: 'TU Munich is the highest-ranked German public university (QS World #37 in 2026). For engineering and CS, RWTH Aachen, KIT, and TU Berlin are equally strong. For medicine and humanities, LMU Munich and Heidelberg University lead. Choose based on subject ranking — not overall ranking — since most German public unis are strong in their specialisation.' },
  { q: 'Are all German public universities free?',
    a: 'Yes, with one exception. All public universities in Berlin, Bavaria, NRW, Hamburg, Hesse, Saxony, Lower Saxony etc. charge €0 tuition. Only Baden-Württemberg introduced a €1,500/semester fee for non-EU students in 2017 — so KIT, Stuttgart, Heidelberg, Tübingen, and Freiburg charge ₹2.6 lakh/year for non-EU students. All others remain tuition-free.' },
  { q: 'What is TU9 in Germany?',
    a: 'TU9 (Technische Universitäten 9) is an alliance of Germany\'s nine leading technical universities — TU Munich, RWTH Aachen, KIT, TU Berlin, TU Darmstadt, TU Dresden, Leibniz Hannover, TU Braunschweig, and University of Stuttgart. TU9 universities account for ~50% of all engineering PhDs in Germany and are the most popular German destinations for Indian engineering students.' },
  { q: 'Can I do my full Master\'s in English at a German public university?',
    a: 'Yes — 1,000+ Masters programmes in Germany are taught entirely in English, especially in STEM, business, and economics. Top options include TU Munich (mostly English Masters), RWTH Aachen (40+ English Masters), TU Berlin, Heidelberg, and Frankfurt. Use the DAAD database to filter by "English-taught" programmes.' },
  { q: 'What GPA do I need for top German public universities?',
    a: 'For TU Munich, KIT, RWTH Aachen, LMU, Heidelberg — minimum CGPA 7.5/10 (75%) is competitive; for TU9 engineering Masters, 8.0/10 (80%) is the typical cutoff for Indian applicants. Mid-tier publics (e.g. Frankfurt, Hannover, Bremen) accept 6.5/10 (65%) with strong work experience or projects.' },
  { q: 'Public vs Private university in Germany — which is better for me?',
    a: 'Public universities are tuition-free, research-heavy, and globally respected — best if you have time (1–1.5 years admission cycle) and a strong academic record. Private universities (IU, GISMA, EBS) charge €10,000–€25,000/year but offer faster admissions (4–6 weeks), 100% English instruction, smaller cohorts, and better industry placement support. For most Indians, the choice is: public if you can wait + qualify, private if you cannot.' },
];

const GermanyPublicUniversitiesPage = () => (
  <GermanyResourceShell
    slug="public-universities"
    breadcrumbLabel="Public Universities"
    articleHeadline="Germany Public Universities 2026 — Complete Guide for Indian Students"
    articleDescription={DESC}
    faqs={FAQS}
    related={['scholarships', 'cost-of-study', 'student-visa']}
  >
    {/* Hero */}
    <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #003087 0%, #001F5C 60%, #020617 100%)' }}>
      <div className="relative max-w-7xl mx-auto px-6 py-16 lg:py-20">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-5">
            <Building2 className="w-3.5 h-3.5 text-[#C5A028]" />
            <span className="text-xs text-white/90 font-medium">Germany Resource — Public Universities</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-syne text-white leading-tight mb-4">
            Germany Public Universities 2026 — Tuition-Free, World-Class Education
          </h1>
          <p className="text-base lg:text-lg text-slate-300 leading-relaxed mb-6">
            Germany operates 400+ public universities — most charging €0 tuition for Indian students. This is the comprehensive 2026 guide: the elite TU9 engineering alliance, top non-TU9 research universities, admission requirements, application steps via Uni-Assist, and how to choose the right institution for your profile.
          </p>
          <div className="grid grid-cols-3 gap-3 max-w-xl">
            <div className="bg-white/5 border border-white/10 rounded-xl p-3">
              <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">Public Unis</div>
              <div className="text-base font-bold text-[#C5A028]">400+</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-3">
              <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">English Masters</div>
              <div className="text-base font-bold text-[#C5A028]">1,000+</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-3">
              <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">Tuition</div>
              <div className="text-base font-bold text-[#C5A028]">€0 / sem</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* TU9 */}
    <section className="py-14 lg:py-18 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex items-center gap-3 mb-4">
          <Trophy className="w-7 h-7 text-[#C5A028]" />
          <h2 className="text-2xl lg:text-3xl font-bold font-syne text-[#003087]">TU9 — Germany's Elite Engineering Alliance</h2>
        </div>
        <p className="text-[15px] leading-7 text-slate-700 mb-6">
          The Technische Universitäten 9 (TU9) alliance is the gold standard for engineering, CS, and applied sciences in Germany. These nine universities collectively grant ~50% of all German engineering PhDs and are the most-applied-to public universities by Indian students.
        </p>
        <div className="overflow-x-auto bg-slate-50 border border-slate-200 rounded-xl">
          <table className="w-full text-sm">
            <thead className="bg-[#003087] text-white">
              <tr>
                <th className="px-4 py-3 text-left font-syne">University</th>
                <th className="px-4 py-3 text-left font-syne">City / State</th>
                <th className="px-4 py-3 text-left font-syne">Strength</th>
                <th className="px-4 py-3 text-left font-syne">QS Ranking</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {TU9.map((u) => (
                <tr key={u.name}>
                  <td className="px-4 py-3 font-bold text-[#003087]">{u.name}</td>
                  <td className="px-4 py-3 text-slate-700">{u.city} <span className="text-slate-400 text-xs">· {u.state}</span></td>
                  <td className="px-4 py-3 text-slate-700">{u.strength}</td>
                  <td className="px-4 py-3 font-semibold">{u.rank}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>

    {/* Top non-TU9 */}
    <section className="py-14 lg:py-18 bg-slate-50">
      <div className="max-w-5xl mx-auto px-6">
        <h2 className="text-2xl lg:text-3xl font-bold font-syne text-[#003087] mb-5">Top Non-TU9 Research Universities</h2>
        <p className="text-sm text-slate-600 mb-6">For medicine, humanities, social sciences, and business — these universities outperform TU9 by a wide margin.</p>
        <div className="grid md:grid-cols-2 gap-4">
          {TOP_NON_TU9.map((u) => (
            <div key={u.name} className="bg-white border border-slate-200 rounded-xl p-5 hover:border-[#003087] transition-all">
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-base font-bold font-syne text-[#003087]">{u.name}</h3>
                <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full uppercase tracking-wider">{u.type}</span>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">{u.strength}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Application Process */}
    <section className="py-14 lg:py-18 bg-white">
      <div className="max-w-5xl mx-auto px-6">
        <h2 className="text-2xl lg:text-3xl font-bold font-syne text-[#003087] mb-7">6-Step Admission Process</h2>
        <div className="space-y-3">
          {ADMISSION_STEPS.map((s) => (
            <div key={s.step} className="bg-slate-50 border border-slate-200 rounded-xl p-5 flex gap-5">
              <div className="w-12 h-12 rounded-xl bg-[#003087] text-white font-bold font-syne text-lg flex items-center justify-center shrink-0">
                {s.step}
              </div>
              <div className="flex-1">
                <h3 className="text-base font-bold font-syne text-slate-900 mb-1.5">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* FAQ */}
    <section className="py-14 lg:py-18 bg-slate-50">
      <div className="max-w-4xl mx-auto px-6">
        <h2 className="text-2xl lg:text-3xl font-bold font-syne text-[#003087] mb-7">Frequently Asked Questions</h2>
        <div className="space-y-3">
          {FAQS.map((f, i) => (
            <details key={i} className="group bg-white border border-slate-200 rounded-xl p-5">
              <summary className="font-syne font-semibold text-slate-900 cursor-pointer text-[15px] flex items-start justify-between">
                <span className="pr-6">{f.q}</span>
                <span className="text-[#003087] group-open:rotate-45 transition-transform text-xl leading-none">+</span>
              </summary>
              <p className="mt-3 text-sm leading-7 text-slate-700">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  </GermanyResourceShell>
);

export default GermanyPublicUniversitiesPage;
